import React, { useEffect, useMemo, useState } from 'react'

import PqrSectionModal from '../pqrPreview/PqrSectionModal'

import { planWpsDefaults } from '../../../utils/wpsRevisionPlanner'

import { formatPqrDate } from '../../../utils/formatPqrDateTime'

import type { ReportListItem } from '../../../utils/reportsClient'

import {
  unionWeldIdsForWpsSelection,
  pqrNumbersFromTrains,
  type WpsPqrTrainOption
} from './readPqrTrainOptions'



const formatHintDate = formatPqrDate



const PILL_TONE = {

  pqr: 'bg-galvanizing-green/10 text-galvanizing-green border border-galvanizing-green/20',

  weld: 'bg-blueprint-blue/10 text-blueprint-blue border border-blueprint-blue/20'

} as const



function MiniPill({

  tone,

  children,

  testId

}: {

  tone: keyof typeof PILL_TONE

  children: React.ReactNode

  testId?: string

}) {

  return (

    <span

      data-testid={testId}

      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ${PILL_TONE[tone]}`}

    >

      {children}

    </span>

  )

}



interface Props {

  open: boolean

  trains: WpsPqrTrainOption[]

  wpsReports: ReportListItem[]

  /** Live PQR reports — used to resolve weld ids from PQR numbers. */
  pqrReports: ReportListItem[]

  initialTrainKeys?: string[] | null

  onApply: (payload: {

    trainKeys: string[]

    pqrNumbers: string[]

    weldIds: string[]

    wpsNumber: string

    revision: string

  }) => void

  onCancel: () => void

}



/**

 * "Select PQR(s) for WPS" modal. Lists existing PQR report trains

 * (real PDFs only) with PQR No. + weld ID pills. Multi-select produces

 * one combined WPS scoped to the union of weld ids.

 */

export default function WpsPqrPickerModal({

  open,

  trains,

  wpsReports,

  pqrReports,

  initialTrainKeys,

  onApply,

  onCancel

}: Props) {

  const seedSelection = (): string[] => {

    if (initialTrainKeys && initialTrainKeys.length > 0) {

      return initialTrainKeys.map((s) => s.trim()).filter(Boolean)

    }

    return []

  }



  const [selected, setSelected] = useState<string[]>(seedSelection)

  const [wpsNumber, setWpsNumber] = useState('')

  const [revision, setRevision] = useState('')

  const [suggestedWpsNumber, setSuggestedWpsNumber] = useState('')

  const [suggestedRevision, setSuggestedRevision] = useState('')



  useEffect(() => {

    if (open) setSelected(seedSelection())

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [open, initialTrainKeys])



  const selectedPqrNumbers = useMemo(
    () => pqrNumbersFromTrains(selected, trains),
    [selected, trains]
  )

  const unionWeldIds = useMemo(
    () =>
      unionWeldIdsForWpsSelection(selected, trains, pqrReports, selectedPqrNumbers),
    [selected, trains, pqrReports, selectedPqrNumbers]
  )

  const unionKey = useMemo(() => unionWeldIds.join('|'), [unionWeldIds])



  useEffect(() => {

    if (unionWeldIds.length === 0) {

      setWpsNumber('')

      setRevision('')

      setSuggestedWpsNumber('')

      setSuggestedRevision('')

      return

    }

    const planned = planWpsDefaults({ reports: wpsReports, weldIds: unionWeldIds })

    setWpsNumber(planned.defaultWpsNumber)

    setRevision(planned.defaultRevision)

    setSuggestedWpsNumber(planned.defaultWpsNumber)

    setSuggestedRevision(planned.defaultRevision)

  }, [unionKey, wpsReports, unionWeldIds])



  const isEmpty = trains.length === 0

  const trimmedWpsNumber = wpsNumber.trim()

  const trimmedRevision = revision.trim()

  const applyDisabledReason =
    selected.length === 0
      ? 'Pick at least one PQR to continue'
      : unionWeldIds.length === 0
        ? 'Selected PQR(s) have no weld scope — regenerate the PQR with weld IDs first.'
        : !trimmedWpsNumber
          ? 'Enter a WPS No. to continue'
          : !trimmedRevision
            ? 'Enter a Rev. to continue'
            : undefined



  const toggleSelected = (trainKey: string) => {

    setSelected((prev) =>

      prev.includes(trainKey) ? prev.filter((k) => k !== trainKey) : [...prev, trainKey]

    )

  }



  const handleApply = () => {

    if (selected.length === 0 || !trimmedWpsNumber || !trimmedRevision) return

    onApply({
      trainKeys: selected,
      pqrNumbers: selectedPqrNumbers,
      weldIds: unionWeldIds,

      wpsNumber: trimmedWpsNumber,

      revision: trimmedRevision

    })

  }



  const handleWpsNumberChange = (next: string) => {

    setWpsNumber(next)

    if (next === suggestedWpsNumber) {

      setRevision(suggestedRevision)

    } else {

      setRevision('0')

    }

  }



  const selectedKey = useMemo(() => [...selected].sort().join('|'), [selected])

  const idListKey = useMemo(() => trains.map((t) => t.trainKey).join('|'), [trains])



  return (

    <PqrSectionModal

      open={open}

      title="Select PQR(s) for WPS"

      isModalDirty={selectedKey !== [...seedSelection()].sort().join('|')}

      applyLabel="Continue"

      applyDisabledReason={applyDisabledReason}

      panelMaxWidthClassName="max-w-2xl"

      onApply={handleApply}

      onCancel={onCancel}

    >

      <div className="flex flex-col gap-4">

        {isEmpty ? (

          <p className="rounded-md border border-hero-red-30 bg-hero-red-10 px-3 py-2 text-sm text-hero-red-90">

            Generate at least one PQR report before creating a WPS.

          </p>

        ) : (

          <fieldset

            className="flex flex-col gap-1"

            key={idListKey}

            data-testid="wps-pqr-picker-list"

          >

            <legend className="sr-only">PQR trains</legend>

            <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white">

              {trains.map((t) => {
                const checked = selected.includes(t.trainKey)
                const hint = t.lastGeneratedHint
                const isAggregated = t.weldIds.length === 0

                return (
                  <label
                    key={t.trainKey}
                    className={`flex flex-col gap-1.5 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 ${
                      isAggregated
                        ? 'cursor-not-allowed bg-gray-50 opacity-60'
                        : `cursor-pointer hover:bg-gray-50 ${checked ? 'bg-brand-red-50/40' : ''}`
                    }`}
                    data-testid={`wps-pqr-option-${t.trainKey}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded text-brand-red-600 focus:ring-brand-red-500 disabled:cursor-not-allowed"
                        checked={checked}
                        disabled={isAggregated}
                        onChange={() => toggleSelected(t.trainKey)}
                      />

                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">

                        <MiniPill tone="pqr" testId={`wps-pqr-pill-${t.trainKey}`}>

                          {t.pqrNumber}

                        </MiniPill>

                        {t.weldIds.length > 0 ? (

                          t.weldIds.map((wid, i) => (

                            <MiniPill

                              key={wid}

                              tone="weld"

                              testId={

                                i === 0

                                  ? `wps-weld-pill-${t.trainKey}`

                                  : `wps-weld-pill-${t.trainKey}-${i}`

                              }

                            >

                              {wid}

                            </MiniPill>

                          ))

                        ) : (

                          <MiniPill tone="weld">—</MiniPill>

                        )}

                      </div>

                    </div>

                    {hint && (

                      <span className="ml-7 text-[11px] text-gray-500">

                        Last generated: {hint.pqrNumber} Rev {hint.revision}

                        {formatHintDate(hint.uploadedAt) &&

                          ` on ${formatHintDate(hint.uploadedAt)}`}

                        {hint.generatedByEmail && ` by ${hint.generatedByEmail}`}

                      </span>

                    )}

                  </label>

                )

              })}

            </div>

          </fieldset>

        )}



        {selected.length > 0 && (

          <div className="grid grid-cols-2 gap-3" data-testid="wps-picker-identifiers">

            <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">

              WPS No.

              <input

                type="text"

                value={wpsNumber}

                onChange={(e) => handleWpsNumberChange(e.target.value)}

                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"

                data-testid="wps-picker-wpsNumber"

                autoComplete="off"

              />

            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">

              Rev.

              <input

                type="text"

                value={revision}

                onChange={(e) => setRevision(e.target.value)}

                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"

                data-testid="wps-picker-revision"

                autoComplete="off"

              />

            </label>

          </div>

        )}



        <p className="text-xs text-gray-500">

          Selected PQR(s) scope Joint Preparation, Pass Sequence, Welding

          Parameters and Welders on the generated WPS via their weld IDs.

        </p>

        <p data-testid="wps-picker-utc-caption" className="text-[11px] italic text-gray-500">

          Times shown in UTC

        </p>

      </div>

    </PqrSectionModal>

  )

}

